// Cliente 360 tabs persisted in the URL path (/clientes/:id/:tab).
// Pure routing domain — no React — so the normalization rules stay
// unit-testable without a router.

export const CLIENTE_TABS = {
  INFO: 'info',
  SERVICIOS: 'servicios',
  FACTURAS: 'facturas',
  PAGOS: 'pagos',
  ESTADO_CUENTA: 'estado-cuenta',
  VISITAS: 'visitas',
  EXCEPCIONES: 'excepciones',
} as const;

export type ClienteTab = (typeof CLIENTE_TABS)[keyof typeof CLIENTE_TABS];

/** Valid tab path segments in render order. */
export const CLIENTE_TAB_ORDER: readonly ClienteTab[] = [
  CLIENTE_TABS.INFO,
  CLIENTE_TABS.SERVICIOS,
  CLIENTE_TABS.FACTURAS,
  CLIENTE_TABS.PAGOS,
  CLIENTE_TABS.ESTADO_CUENTA,
  CLIENTE_TABS.VISITAS,
  CLIENTE_TABS.EXCEPCIONES,
];

function isClienteTab(value: string): value is ClienteTab {
  return (CLIENTE_TAB_ORDER as readonly string[]).includes(value);
}

export interface ClienteTabResolution {
  tab: ClienteTab;
  /** True when the URL segment must be replaced by the client root. */
  needsRedirect: boolean;
}

/**
 * Resolve the :tab URL segment into a valid tab.
 * - `/clientes/:id` (no segment) means info, no redirect.
 * - `/clientes/:id/info` is accepted as an alias of the root.
 * - Unknown segments (including `edit`, which owns its dedicated route but
 *   defensively normalizes here too) redirect to the client root so the page
 *   never renders blank.
 * - `facturas` without COBROS_READ redirects to the client root.
 * - `pagos` without PAGOS_READ redirects to the client root.
 * - `visitas` without VISITAS_READ redirects to the client root.
 */
export function resolveClienteTab(
  segment: string | undefined,
  canReadCobros: boolean,
  canReadEstadoCuenta = false,
  canReadPagos = false,
  canReadVisitas = false,
): ClienteTabResolution {
  if (segment === undefined) {
    return { tab: CLIENTE_TABS.INFO, needsRedirect: false };
  }
  if (!isClienteTab(segment)) {
    return { tab: CLIENTE_TABS.INFO, needsRedirect: true };
  }
  if (segment === CLIENTE_TABS.FACTURAS && !canReadCobros) {
    return { tab: CLIENTE_TABS.INFO, needsRedirect: true };
  }
  if (segment === CLIENTE_TABS.ESTADO_CUENTA && !canReadEstadoCuenta) {
    return { tab: CLIENTE_TABS.INFO, needsRedirect: true };
  }
  if (segment === CLIENTE_TABS.PAGOS && !canReadPagos) {
    return { tab: CLIENTE_TABS.INFO, needsRedirect: true };
  }
  if (segment === CLIENTE_TABS.VISITAS && !canReadVisitas) {
    return { tab: CLIENTE_TABS.INFO, needsRedirect: true };
  }
  return { tab: segment, needsRedirect: false };
}
